import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Link, ExternalLink, Webhook, RefreshCcw } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов
import RedirectCheckerForm from './UrlRedirectCheckerPage/components/RedirectCheckerForm';
import ResultsViewer from './UrlRedirectCheckerPage/components/ResultsViewer';
import RedirectMap from './UrlRedirectCheckerPage/components/RedirectMap';
import InfoContent from './UrlRedirectCheckerPage/components/InfoContent';
import PopularRedirects from './UrlRedirectCheckerPage/components/PopularRedirects';
import BulkChecker from './UrlRedirectCheckerPage/components/BulkChecker';
import RedirectAnalyzer from './UrlRedirectCheckerPage/components/RedirectAnalyzer';
import HttpStatusList from './UrlRedirectCheckerPage/components/HttpStatusList';
import HistoryTracker from './UrlRedirectCheckerPage/components/HistoryTracker';
import DomainsToWatch from './UrlRedirectCheckerPage/components/DomainsToWatch';

// Определение начального состояния формы
export const initialFormData = {
  url: '',
  method: 'GET',
  followRedirects: true,
  maxRedirects: 10,
  timeout: 30000,
  userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/119.0.0.0 Safari/537.36',
  headers: [],
  cookies: [],
  useProxy: false,
  proxyLocation: 'russia',
  customProxy: '',
  includeHeaders: true,
  includeCookies: true,
  saveHistory: true,
  validateSsl: true,
};

// Определение методов HTTP-запросов
export const httpMethods = [
  { id: 'GET', name: 'GET' },
  { id: 'HEAD', name: 'HEAD' },
  { id: 'POST', name: 'POST' },
  { id: 'PUT', name: 'PUT' },
  { id: 'DELETE', name: 'DELETE' },
  { id: 'OPTIONS', name: 'OPTIONS' },
];

// Локации прокси-серверов с акцентом на Россию
export const proxyLocations = [
  { id: 'russia', name: 'Россия (автовыбор города)' },
  { id: 'moscow', name: 'Москва' },
  { id: 'stpetersburg', name: 'Санкт-Петербург' },
  { id: 'ekaterinburg', name: 'Екатеринбург' },
  { id: 'novosibirsk', name: 'Новосибирск' },
  { id: 'europe', name: 'Европа' },
  { id: 'usa', name: 'США' },
  { id: 'asia', name: 'Азия' },
  { id: 'custom', name: 'Указать свой' },
];

/**
 * UrlRedirectCheckerPage Component - Полнофункциональный инструмент для проверки перенаправлений URL
 * с отслеживанием цепочек редиректов, анализом HTTP статусов и визуализацией пути.
 */
const UrlRedirectCheckerPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('url-redirect-checker'), []);
  const pageTitle = tool?.metaTitle || 'Проверка перенаправлений URL | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный инструмент для проверки цепочек редиректов (301, 302, 303, 307, 308) и анализа перенаправлений URL. Отслеживайте путь редиректов, время загрузки и HTTP-заголовки для SEO-оптимизации и безопасности.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [errors, setErrors] = useState({});
  const [history, setHistory] = useState([]);
  const [savedDomains, setSavedDomains] = useState([]);
  const [bulkResults, setBulkResults] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка истории из localStorage ---
  useEffect(() => {
    const historyData = localStorage.getItem('redirectCheckerHistory');
    if (historyData) {
      try {
        setHistory(JSON.parse(historyData));
      } catch (e) {
        console.error("Error loading history:", e);
      }
    }
    
    const savedDomainsData = localStorage.getItem('savedDomains');
    if (savedDomainsData) {
      try {
        setSavedDomains(JSON.parse(savedDomainsData));
      } catch (e) {
        console.error("Error loading saved domains:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'seo-redirects':
          newState.userAgent = 'Googlebot/2.1 (+http://www.google.com/bot.html)';
          newState.followRedirects = true;
          break;
        case 'check-canonical':
          newState.includeHeaders = true;
          newState.followRedirects = false;
          break;
        case 'security-check':
          newState.validateSsl = true;
          newState.includeCookies = true;
          break;
        case 'international':
          newState.useProxy = true;
          newState.proxyLocation = 'europe';
          break;
        case 'bulk-check':
          // Для массовой проверки оставляем базовые настройки
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});
    setBulkResults([]);

  }, [subToolId]);

  // Функция проверки URL
  const checkUrl = async (url, options = {}) => {
    // В реальном приложении здесь был бы AJAX-запрос к бэкенду
    // Эмулирую работу API для демонстрации

    setIsChecking(true);
    
    try {
      // Имитация задержки запроса
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Проверка URL на валидность
      if (!url || !url.trim()) {
        throw new Error('URL обязателен для заполнения');
      }
      
      let checkedUrl = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        checkedUrl = 'https://' + url;
      }
      
      // Имитируем результаты проверки редиректов
      // В реальном приложении эти данные придут от API
      const redirectChain = [];
      const mainDomain = new URL(checkedUrl).hostname;
      
      // Добавляем исходный URL
      redirectChain.push({
        url: checkedUrl,
        status: 302,
        statusText: 'Found',
        headers: {
          'location': checkedUrl.includes('example.com') ? 
            checkedUrl.replace('example.com', 'www.example.com') : 
            checkedUrl + '/ru/',
          'content-type': 'text/html; charset=UTF-8',
          'server': 'nginx/1.22.0',
          'set-cookie': ['session=abc123; Path=/; HttpOnly', 'region=ru; Path=/']
        },
        timing: {
          dns: 21,
          connection: 56,
          tls: 128,
          ttfb: 203,
          total: 487
        },
        ip: '192.168.1.1',
        location: 'Москва, Россия',
        timestamp: new Date().toISOString(),
        isFinal: false
      });
      
      // Если домен существующий, добавляем цепочку редиректов
      if (mainDomain.includes('example') || mainDomain.includes('google') || 
          mainDomain.includes('yandex') || mainDomain.includes('mail.ru')) {
        
        // Добавляем первое перенаправление
        const firstRedirect = redirectChain[0].headers.location;
        redirectChain.push({
          url: firstRedirect,
          status: 301,
          statusText: 'Moved Permanently',
          headers: {
            'location': firstRedirect.endsWith('/ru/') ? firstRedirect : firstRedirect + 'main/',
            'content-type': 'text/html; charset=UTF-8',
            'server': 'nginx/1.22.0',
            'cache-control': 'max-age=3600'
          },
          timing: {
            dns: 0, // DNS уже закэширован
            connection: 32,
            tls: 92,
            ttfb: 187,
            total: 311
          },
          ip: '192.168.1.2',
          location: 'Москва, Россия',
          timestamp: new Date().toISOString(),
          isFinal: false
        });
        
        // Добавляем финальное перенаправление
        const finalUrl = redirectChain[1].headers.location;
        redirectChain.push({
          url: finalUrl,
          status: 200,
          statusText: 'OK',
          headers: {
            'content-type': 'text/html; charset=UTF-8',
            'server': 'nginx/1.22.0',
            'cache-control': 'max-age=3600',
            'content-length': '45721',
            'link': '<https://www.example.com>; rel="canonical"'
          },
          timing: {
            dns: 0,
            connection: 28,
            tls: 87,
            ttfb: 178,
            total: 543
          },
          ip: '192.168.1.3',
          location: 'Москва, Россия',
          timestamp: new Date().toISOString(),
          isFinal: true,
          meta: {
            title: 'Пример страницы | Тестовый сайт',
            description: 'Это мета-описание страницы для тестирования инструмента проверки редиректов.',
            canonical: 'https://www.example.com/ru/main/',
            robotsDirectives: 'index, follow'
          }
        });
      } else {
        // Для других доменов возвращаем 404 или ошибку соединения
        redirectChain[0].status = Math.random() > 0.5 ? 404 : 0;
        redirectChain[0].statusText = redirectChain[0].status === 0 ? 'Connection Error' : 'Not Found';
        redirectChain[0].isFinal = true;
        redirectChain[0].error = redirectChain[0].status === 0 ? 
          'Не удалось установить соединение с сервером' : 
          'Страница не найдена';
        
        // Если статус 404, добавляем некоторые заголовки
        if (redirectChain[0].status === 404) {
          redirectChain[0].headers = {
            'content-type': 'text/html; charset=UTF-8',
            'server': 'nginx/1.22.0'
          };
        } else {
          redirectChain[0].headers = {};
        }
      }
      
      // Имитация сводки результатов
      const summary = {
        initialUrl: checkedUrl,
        finalUrl: redirectChain[redirectChain.length - 1].url,
        redirectCount: redirectChain.length - 1,
        hasErrors: redirectChain[redirectChain.length - 1].status >= 400 || redirectChain[redirectChain.length - 1].status === 0,
        totalTime: redirectChain.reduce((sum, hop) => sum + hop.timing.total, 0),
        isSameDomain: new URL(checkedUrl).hostname === new URL(redirectChain[redirectChain.length - 1].url).hostname,
        isSecureConnection: redirectChain[redirectChain.length - 1].url.startsWith('https://'),
        hasInfiniteLoop: false,
        seoImpact: getSeoImpactRating(redirectChain)
      };
      
      // Сохраняем в историю
      if (options.saveHistory !== false) {
        const newHistory = [
          {
            id: Date.now().toString(),
            url: checkedUrl,
            date: new Date().toISOString(),
            summary
          },
          ...history
        ].slice(0, 100); // Ограничиваем историю 100 записями
        
        setHistory(newHistory);
        localStorage.setItem('redirectCheckerHistory', JSON.stringify(newHistory));
      }
      
      // Возвращаем результаты
      return {
        chain: redirectChain,
        summary,
        options: { ...formData, ...options }
      };
    } catch (error) {
      console.error("Error checking URL:", error);
      throw error;
    } finally {
      setIsChecking(false);
    }
  };

  // Вспомогательная функция для оценки SEO-влияния редиректов
  const getSeoImpactRating = (redirectChain) => {
    // Упрощенная логика оценки SEO-влияния редиректов
    const redirectCount = redirectChain.length - 1;
    const finalStatus = redirectChain[redirectChain.length - 1].status;
    const hasBadStatus = redirectChain.some(hop => hop.status >= 400);
    const hasTempRedirects = redirectChain.some(hop => [302, 307].includes(hop.status));
    
    if (hasBadStatus || finalStatus >= 400) {
      return {
        score: 'bad',
        label: 'Плохо',
        reasons: ['Найдены ошибочные статусы (4xx или 5xx)', 'Поисковые системы могут не проиндексировать страницу']
      };
    } else if (redirectCount > 2) {
      return {
        score: 'poor',
        label: 'Проблемно',
        reasons: ['Слишком длинная цепочка редиректов (более 2)', 'Замедляет загрузку и ухудшает опыт пользователя']
      };
    } else if (hasTempRedirects) {
      return {
        score: 'fair',
        label: 'Удовлетворительно',
        reasons: ['Найдены временные редиректы 302/307', 'Рекомендуется заменить на постоянные 301/308']
      };
    } else if (redirectCount === 0) {
      return {
        score: 'excellent',
        label: 'Отлично',
        reasons: ['Нет редиректов, прямая загрузка страницы']
      };
    } else {
      return {
        score: 'good',
        label: 'Хорошо',
        reasons: ['Используются постоянные редиректы 301/308', 'Оптимальная длина цепочки редиректов']
      };
    }
  };

  // Проверка одного URL
  const handleCheckUrl = async () => {
    try {
      setErrors({});
      const result = await checkUrl(formData.url, formData);
      setResults(result);
    } catch (error) {
      setErrors({ url: error.message });
    }
  };

  // Проверка нескольких URL
  const handleBulkCheck = async (urls) => {
    setBulkResults([]);
    const results = [];
    
    for (const url of urls) {
      try {
        const result = await checkUrl(url, { ...formData, saveHistory: false });
        results.push({ url, result, error: null });
      } catch (error) {
        results.push({ url, result: null, error: error.message });
      }
    }
    
    setBulkResults(results);
  };

  // Сохранение домена для отслеживания
  const saveDomain = (domain, name) => {
    if (!domain) return;
    
    const domainObj = {
      id: Date.now().toString(),
      domain,
      name: name || `Домен ${domain}`,
      dateAdded: new Date().toISOString(),
      lastChecked: null,
      status: 'pending'
    };
    
    const updatedDomains = [...savedDomains, domainObj];
    setSavedDomains(updatedDomains);
    localStorage.setItem('savedDomains', JSON.stringify(updatedDomains));
    
    return domainObj;
  };

  // Удаление сохраненного домена
  const deleteSavedDomain = (id) => {
    const updatedDomains = savedDomains.filter(domain => domain.id !== id);
    setSavedDomains(updatedDomains);
    localStorage.setItem('savedDomains', JSON.stringify(updatedDomains));
  };

  // Очистка истории проверок
  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('redirectCheckerHistory');
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <ExternalLink className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Проверка перенаправлений URL'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма проверки URL --- */}
            <RedirectCheckerForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isChecking={isChecking}
              onCheckUrl={handleCheckUrl}
              httpMethods={httpMethods}
              proxyLocations={proxyLocations}
              showAdvancedOptions={showAdvancedOptions}
              setShowAdvancedOptions={setShowAdvancedOptions}
            />

            {/* --- Результаты проверки --- */}
            {results && !isChecking && (
              <>
                <ResultsViewer results={results} />
                <RedirectMap redirectChain={results.chain} />
              </>
            )}
            
            {/* --- Массовая проверка URL --- */}
            <BulkChecker 
              onBulkCheck={handleBulkCheck}
              bulkResults={bulkResults}
              isChecking={isChecking}
            />

            {/* --- Анализатор редиректов --- */}
            <RedirectAnalyzer />

            {/* --- Информация о HTTP-статусах --- */}
            <HttpStatusList />

            {/* --- Информационный контент --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Популярные редиректы --- */}
            <PopularRedirects 
              onCheckUrl={(url) => {
                setFormData(prev => ({ ...prev, url }));
                checkUrl(url).then(setResults);
              }}
            />

            {/* --- История проверок --- */}
            <HistoryTracker
              history={history}
              onClearHistory={clearHistory}
              onCheckUrl={(url) => {
                setFormData(prev => ({ ...prev, url }));
                checkUrl(url).then(setResults);
              }}
            />

            {/* --- Отслеживаемые домены --- */}
            <DomainsToWatch
              domains={savedDomains}
              onSaveDomain={saveDomain}
              onDeleteDomain={deleteSavedDomain}
              onCheckDomain={(domain) => {
                setFormData(prev => ({ ...prev, url: domain }));
                checkUrl(domain).then(setResults);
              }}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .custom-scrollbar {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default UrlRedirectCheckerPage;